import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { revertConfirmationCopy, type RevertAdoptionResult, type RoleAdoptionStatus } from '@/lib/roleAdoption';
import { revertRoleAdoption } from '@/services/roleAdoptionService';

/**
 * RBAC-3.4 · FR-2b. Reverse an unedited adoption: the same people go back, the copy is deleted.
 * `revert_role_adoption()` refuses (22023) if the copy changed since this was read; that sentence is
 * shown as it comes, because it says what changed.
 */
export interface RevertAdoptionDialogProps {
  open: boolean;
  status: Extract<RoleAdoptionStatus, { adopted: true }> | null;
  copyName: string;
  onOpenChange: (open: boolean) => void;
  onReverted: (result: RevertAdoptionResult) => void;
}

export function RevertAdoptionDialog({ open, status, copyName, onOpenChange, onReverted }: RevertAdoptionDialogProps) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const copy = status ? revertConfirmationCopy(status, copyName) : null;

  const confirm = async () => {
    if (!status) return;
    setSaving(true);
    setError(null);
    try {
      const r = await revertRoleAdoption(status.roleId);
      onReverted(r);
      onOpenChange(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The adoption could not be reversed.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <AlertDialog
      open={open}
      onOpenChange={(o) => {
        if (saving) return;
        if (!o) setError(null);
        onOpenChange(o);
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{copy?.title ?? 'Reverse adoption'}</AlertDialogTitle>
          {copy && <AlertDialogDescription>{copy.summary}</AlertDialogDescription>}
        </AlertDialogHeader>
        {error && <p className="text-sm text-destructive">{error}</p>}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={saving}>Cancel</AlertDialogCancel>
          <Button onClick={confirm} disabled={!status || saving}>
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {copy?.confirmLabel ?? 'Reverse'}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export default RevertAdoptionDialog;
